import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { fileService } from '../../services/api'
import { 
  FileAttachment, 
  FileCategory, 
  UploadFileRequest,
  PaginationParams
} from '../../types/api'
import { isApiError } from '../../utils/api'

interface FileState {
  files: FileAttachment[]
  currentFile: FileAttachment | null
  pagination: {
    page: number
    per_page: number
    total: number
    last_page: number
  }
  filters: {
    category?: FileCategory
    file_type?: string
    invoice_id?: string
    search?: string
  }
  loading: boolean
  uploading: boolean
  deleting: boolean
  error: string | null
  uploadProgress: Record<string, number> // file name -> progress percentage
  uploadQueue: Array<{
    file: File
    category: FileCategory
    invoice_id?: string
    progress: number
    status: 'pending' | 'uploading' | 'completed' | 'failed'
    error?: string
  }>
  recentFiles: FileAttachment[]
  recentlyDeleted: Array<{
    file: FileAttachment
    deleted_at: string
    can_restore: boolean
  }>
  categories: Array<{
    category: FileCategory
    count: number
    total_size: number
  }>
  storageStatistics: {
    total_files: number
    total_size: number
    used_space: number
    available_space: number
    quota: number
    quota_percentage: number
    by_category: Record<FileCategory, { count: number, size: number }>
    by_type: Record<string, { count: number, size: number }>
  } | null
  searchResults: FileAttachment[]
  searchLoading: boolean
  previewData: {
    file_id: string
    can_preview: boolean
    preview_url?: string
    preview_type?: 'image' | 'pdf' | 'text' | 'video'
  } | null
  shareData: {
    file_id: string
    share_url: string
    share_token: string
    expires_at?: string
  } | null
  validationResult: {
    valid: boolean
    errors: string[]
    warnings: string[]
    size_human: string
    type: string
  } | null
  uploadConstraints: {
    max_file_size: number
    max_files_per_upload: number
    allowed_types: string[]
    allowed_extensions: string[]
    max_total_size: number
  } | null
  bulkOperationStatus: {
    operation: 'delete' | 'move' | 'download' | null
    progress: number
    completed: number
    failed: number
    total: number
  }
}

const initialState: FileState = {
  files: [],
  currentFile: null,
  pagination: {
    page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  },
  filters: {},
  loading: false,
  uploading: false,
  deleting: false,
  error: null,
  uploadProgress: {},
  uploadQueue: [],
  recentFiles: [],
  recentlyDeleted: [],
  categories: [],
  storageStatistics: null,
  searchResults: [],
  searchLoading: false,
  previewData: null,
  shareData: null,
  validationResult: null,
  uploadConstraints: null,
  bulkOperationStatus: {
    operation: null,
    progress: 0,
    completed: 0,
    failed: 0,
    total: 0
  }
}

// Async thunks
export const fetchFiles = createAsyncThunk(
  'files/fetchFiles',
  async (params: { 
    filters?: typeof initialState.filters; 
    pagination?: PaginationParams 
  }, { rejectWithValue }) => {
    try {
      const response = await fileService.getFiles(params.filters, params.pagination)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch files')
    }
  }
)

export const fetchFile = createAsyncThunk(
  'files/fetchFile',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await fileService.getFile(id)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch file')
    }
  }
)

export const uploadFile = createAsyncThunk(
  'files/uploadFile',
  async (data: UploadFileRequest, { rejectWithValue, dispatch }) => {
    try {
      // Start tracking upload progress
      dispatch(addToUploadQueue({
        file: data.file,
        category: data.category,
        invoice_id: data.invoice_id,
        progress: 0,
        status: 'uploading'
      }))

      const response = await fileService.uploadFile(data)
      
      // Mark as completed
      dispatch(updateUploadProgress({
        fileName: data.file.name,
        progress: 100,
        status: 'completed'
      }))

      return response.data
    } catch (error) {
      // Mark as failed
      dispatch(updateUploadProgress({
        fileName: data.file.name,
        progress: 0,
        status: 'failed',
        error: error instanceof Error ? error.message : 'Upload failed'
      }))

      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to upload file')
    }
  }
)

export const uploadMultipleFiles = createAsyncThunk(
  'files/uploadMultipleFiles',
  async (data: {
    files: File[]
    category: FileCategory
    invoice_id?: string
  }, { rejectWithValue, dispatch }) => {
    try {
      // Add all files to upload queue
      data.files.forEach(file => {
        dispatch(addToUploadQueue({
          file,
          category: data.category,
          invoice_id: data.invoice_id,
          progress: 0,
          status: 'uploading'
        }))
      })

      const response = await fileService.uploadMultipleFiles(data)
      
      // Update upload status for all files
      data.files.forEach(file => {
        const wasUploaded = response.data.uploaded.some(uploaded => 
          uploaded.name === file.name
        )
        const failed = response.data.failed.find(f => f.file === file.name)

        dispatch(updateUploadProgress({
          fileName: file.name,
          progress: wasUploaded ? 100 : 0,
          status: wasUploaded ? 'completed' : 'failed',
          error: failed?.error
        }))
      })

      return response.data
    } catch (error) {
      // Mark all as failed
      data.files.forEach(file => {
        dispatch(updateUploadProgress({
          fileName: file.name,
          progress: 0,
          status: 'failed',
          error: error instanceof Error ? error.message : 'Upload failed'
        }))
      })

      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to upload files')
    }
  }
)

export const deleteFile = createAsyncThunk(
  'files/deleteFile',
  async (id: string, { rejectWithValue }) => {
    try {
      await fileService.deleteFile(id)
      return id
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to delete file')
    }
  }
)

export const updateFile = createAsyncThunk(
  'files/updateFile',
  async ({ id, data }: { 
    id: string; 
    data: { name?: string; category?: FileCategory } 
  }, { rejectWithValue }) => {
    try {
      const response = await fileService.updateFile(id, data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to update file')
    }
  }
)

export const searchFiles = createAsyncThunk(
  'files/searchFiles',
  async ({ query, filters }: { 
    query: string; 
    filters?: any 
  }, { rejectWithValue }) => {
    try {
      const response = await fileService.searchFiles(query, filters)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to search files')
    }
  }
)

export const getFilePreview = createAsyncThunk(
  'files/getPreview',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await fileService.getPreview(id)
      return { file_id: id, ...response.data }
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to get file preview')
    }
  }
)

export const shareFile = createAsyncThunk(
  'files/shareFile',
  async ({ id, options }: { 
    id: string; 
    options: {
      expires_at?: string
      password?: string
      allow_download?: boolean
      allow_preview?: boolean
    }
  }, { rejectWithValue }) => {
    try {
      const response = await fileService.shareFile(id, options)
      return { file_id: id, ...response.data }
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to share file')
    }
  }
)

export const validateFile = createAsyncThunk(
  'files/validateFile',
  async (file: File, { rejectWithValue }) => {
    try {
      const response = await fileService.validateFile(file)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to validate file')
    }
  }
)

export const fetchUploadConstraints = createAsyncThunk(
  'files/fetchUploadConstraints',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fileService.getUploadConstraints()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch upload constraints')
    }
  }
)

export const fetchStorageStatistics = createAsyncThunk(
  'files/fetchStorageStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fileService.getStorageStatistics()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch storage statistics')
    }
  }
)

export const fetchCategories = createAsyncThunk(
  'files/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fileService.getCategories()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch categories')
    }
  }
)

export const fetchRecentFiles = createAsyncThunk(
  'files/fetchRecentFiles',
  async (limit: number = 10, { rejectWithValue }) => {
    try {
      const response = await fileService.getRecentFiles(limit)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch recent files')
    }
  }
)

export const fetchRecentlyDeleted = createAsyncThunk(
  'files/fetchRecentlyDeleted',
  async (limit: number = 10, { rejectWithValue }) => {
    try {
      const response = await fileService.getRecentlyDeleted(limit)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch recently deleted files')
    }
  }
)

export const restoreFile = createAsyncThunk(
  'files/restoreFile',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await fileService.restoreFile(id)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to restore file')
    }
  }
)

export const bulkDeleteFiles = createAsyncThunk(
  'files/bulkDelete',
  async (ids: string[], { rejectWithValue }) => {
    try {
      const response = await fileService.bulkDelete(ids)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to bulk delete files')
    }
  }
)

export const bulkMoveFiles = createAsyncThunk(
  'files/bulkMove',
  async ({ ids, category }: { ids: string[]; category: FileCategory }, { rejectWithValue }) => {
    try {
      const response = await fileService.bulkMove(ids, category)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to bulk move files')
    }
  }
)

// Create slice
const fileSlice = createSlice({
  name: 'files',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setFilters: (state, action: PayloadAction<typeof initialState.filters>) => {
      state.filters = action.payload
    },
    updateFilters: (state, action: PayloadAction<Partial<typeof initialState.filters>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = {}
    },
    setPagination: (state, action: PayloadAction<Partial<typeof initialState.pagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    setCurrentFile: (state, action: PayloadAction<FileAttachment | null>) => {
      state.currentFile = action.payload
    },
    clearSearchResults: (state) => {
      state.searchResults = []
    },
    clearPreviewData: (state) => {
      state.previewData = null
    },
    clearShareData: (state) => {
      state.shareData = null
    },
    clearValidationResult: (state) => {
      state.validationResult = null
    },
    addToUploadQueue: (state, action: PayloadAction<{
      file: File
      category: FileCategory
      invoice_id?: string
      progress: number
      status: 'pending' | 'uploading' | 'completed' | 'failed'
      error?: string
    }>) => {
      state.uploadQueue.push(action.payload)
    },
    updateUploadProgress: (state, action: PayloadAction<{
      fileName: string
      progress: number
      status?: 'pending' | 'uploading' | 'completed' | 'failed'
      error?: string
    }>) => {
      const { fileName, progress, status, error } = action.payload
      state.uploadProgress[fileName] = progress
      
      const queueItem = state.uploadQueue.find(item => item.file.name === fileName)
      if (queueItem) {
        queueItem.progress = progress
        if (status) queueItem.status = status
        if (error) queueItem.error = error
      }
    },
    removeFromUploadQueue: (state, action: PayloadAction<string>) => {
      state.uploadQueue = state.uploadQueue.filter(item => item.file.name !== action.payload)
      delete state.uploadProgress[action.payload]
    },
    clearUploadQueue: (state) => {
      state.uploadQueue = []
      state.uploadProgress = {}
    },
    resetBulkOperationStatus: (state) => {
      state.bulkOperationStatus = {
        operation: null,
        progress: 0,
        completed: 0,
        failed: 0,
        total: 0
      }
    }
  },
  extraReducers: (builder) => {
    // Fetch Files
    builder
      .addCase(fetchFiles.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFiles.fulfilled, (state, action) => {
        state.loading = false
        state.files = action.payload.data
        state.pagination = {
          page: action.payload.page,
          per_page: action.payload.per_page,
          total: action.payload.total,
          last_page: action.payload.last_page
        }
      })
      .addCase(fetchFiles.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch Single File
    builder
      .addCase(fetchFile.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFile.fulfilled, (state, action) => {
        state.loading = false
        state.currentFile = action.payload
      })
      .addCase(fetchFile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Upload File
    builder
      .addCase(uploadFile.pending, (state) => {
        state.uploading = true
        state.error = null
      })
      .addCase(uploadFile.fulfilled, (state, action) => {
        state.uploading = false
        state.files.unshift(action.payload)
        state.currentFile = action.payload
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.uploading = false
        state.error = action.payload as string
      })

    // Upload Multiple Files
    builder
      .addCase(uploadMultipleFiles.pending, (state) => {
        state.uploading = true
        state.error = null
      })
      .addCase(uploadMultipleFiles.fulfilled, (state, action) => {
        state.uploading = false
        // Add successfully uploaded files to the beginning of the list
        state.files.unshift(...action.payload.uploaded)
      })
      .addCase(uploadMultipleFiles.rejected, (state, action) => {
        state.uploading = false
        state.error = action.payload as string
      })

    // Delete File
    builder
      .addCase(deleteFile.pending, (state) => {
        state.deleting = true
        state.error = null
      })
      .addCase(deleteFile.fulfilled, (state, action) => {
        state.deleting = false
        state.files = state.files.filter(file => file.id !== action.payload)
        if (state.currentFile?.id === action.payload) {
          state.currentFile = null
        }
      })
      .addCase(deleteFile.rejected, (state, action) => {
        state.deleting = false
        state.error = action.payload as string
      })

    // Update File
    builder
      .addCase(updateFile.fulfilled, (state, action) => {
        const index = state.files.findIndex(file => file.id === action.payload.id)
        if (index !== -1) {
          state.files[index] = action.payload
        }
        if (state.currentFile?.id === action.payload.id) {
          state.currentFile = action.payload
        }
      })
      .addCase(updateFile.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Search Files
    builder
      .addCase(searchFiles.pending, (state) => {
        state.searchLoading = true
      })
      .addCase(searchFiles.fulfilled, (state, action) => {
        state.searchLoading = false
        state.searchResults = action.payload
      })
      .addCase(searchFiles.rejected, (state, action) => {
        state.searchLoading = false
        state.error = action.payload as string
      })

    // Get File Preview
    builder
      .addCase(getFilePreview.fulfilled, (state, action) => {
        state.previewData = action.payload
      })
      .addCase(getFilePreview.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Share File
    builder
      .addCase(shareFile.fulfilled, (state, action) => {
        state.shareData = action.payload
      })
      .addCase(shareFile.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Validate File
    builder
      .addCase(validateFile.fulfilled, (state, action) => {
        state.validationResult = action.payload
      })
      .addCase(validateFile.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch Upload Constraints
    builder
      .addCase(fetchUploadConstraints.fulfilled, (state, action) => {
        state.uploadConstraints = action.payload
      })
      .addCase(fetchUploadConstraints.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch Storage Statistics
    builder
      .addCase(fetchStorageStatistics.fulfilled, (state, action) => {
        state.storageStatistics = action.payload
      })
      .addCase(fetchStorageStatistics.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch Categories
    builder
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch Recent Files
    builder
      .addCase(fetchRecentFiles.fulfilled, (state, action) => {
        state.recentFiles = action.payload
      })
      .addCase(fetchRecentFiles.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch Recently Deleted
    builder
      .addCase(fetchRecentlyDeleted.fulfilled, (state, action) => {
        state.recentlyDeleted = action.payload
      })
      .addCase(fetchRecentlyDeleted.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Restore File
    builder
      .addCase(restoreFile.fulfilled, (state, action) => {
        state.files.unshift(action.payload)
        state.recentlyDeleted = state.recentlyDeleted.filter(
          item => item.file.id !== action.payload.id
        )
      })
      .addCase(restoreFile.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Bulk Operations
    builder
      .addCase(bulkDeleteFiles.pending, (state) => {
        state.bulkOperationStatus.operation = 'delete'
      })
      .addCase(bulkDeleteFiles.fulfilled, (state, action) => {
        state.bulkOperationStatus = {
          operation: null,
          progress: 100,
          completed: action.payload.deleted,
          failed: action.payload.failed.length,
          total: action.payload.deleted + action.payload.failed.length
        }
      })
      .addCase(bulkDeleteFiles.rejected, (state, action) => {
        state.bulkOperationStatus.operation = null
        state.error = action.payload as string
      })

    builder
      .addCase(bulkMoveFiles.pending, (state) => {
        state.bulkOperationStatus.operation = 'move'
      })
      .addCase(bulkMoveFiles.fulfilled, (state, action) => {
        state.bulkOperationStatus = {
          operation: null,
          progress: 100,
          completed: action.payload.moved,
          failed: action.payload.failed.length,
          total: action.payload.moved + action.payload.failed.length
        }
      })
      .addCase(bulkMoveFiles.rejected, (state, action) => {
        state.bulkOperationStatus.operation = null
        state.error = action.payload as string
      })
  },
})

// Export actions
export const {
  clearError,
  setFilters,
  updateFilters,
  clearFilters,
  setPagination,
  setCurrentFile,
  clearSearchResults,
  clearPreviewData,
  clearShareData,
  clearValidationResult,
  addToUploadQueue,
  updateUploadProgress,
  removeFromUploadQueue,
  clearUploadQueue,
  resetBulkOperationStatus
} = fileSlice.actions

// Selectors
export const selectFiles = (state: { files: FileState }) => state.files.files
export const selectCurrentFile = (state: { files: FileState }) => state.files.currentFile
export const selectFilePagination = (state: { files: FileState }) => state.files.pagination
export const selectFileFilters = (state: { files: FileState }) => state.files.filters
export const selectFileLoading = (state: { files: FileState }) => state.files.loading
export const selectFileError = (state: { files: FileState }) => state.files.error
export const selectUploadProgress = (state: { files: FileState }) => state.files.uploadProgress
export const selectUploadQueue = (state: { files: FileState }) => state.files.uploadQueue
export const selectFileSearchResults = (state: { files: FileState }) => state.files.searchResults
export const selectRecentFiles = (state: { files: FileState }) => state.files.recentFiles
export const selectFileCategories = (state: { files: FileState }) => state.files.categories
export const selectStorageStatistics = (state: { files: FileState }) => state.files.storageStatistics
export const selectPreviewData = (state: { files: FileState }) => state.files.previewData
export const selectShareData = (state: { files: FileState }) => state.files.shareData
export const selectValidationResult = (state: { files: FileState }) => state.files.validationResult
export const selectUploadConstraints = (state: { files: FileState }) => state.files.uploadConstraints
export const selectFileBulkOperationStatus = (state: { files: FileState }) => state.files.bulkOperationStatus

export default fileSlice.reducer 